import React from 'react';
import { X } from 'lucide-react';
import { Goal } from '../types';

interface GoalProgressItem {
  id: string;
  progress: number;
  percentage: number;
  isComplete: boolean;
  isClaimed: boolean;
}

interface GoalsModalProps {
  showGoals: boolean;
  setShowGoals: (show: boolean) => void;
  goals: Goal[];
  goalProgress: GoalProgressItem[];
  completedGoals: string[];
  claimedGoals: string[];
  claimGoal: (goalId: string) => boolean;
  formatCurrency: (amount: number, decimals?: number) => string;
}

const GoalsModal: React.FC<GoalsModalProps> = ({
  showGoals,
  setShowGoals,
  goals,
  goalProgress,
  completedGoals,
  claimedGoals,
  claimGoal,
  formatCurrency
}) => {
  if (!showGoals) return null;

  // Group goals by category
  const goalsByCategory = goals.reduce((acc: Record<string, Goal[]>, goal: Goal) => {
    const category = goal.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(goal);
    return acc;
  }, {});

  // Format category names for display
  const formatCategoryName = (category: string): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Goals & Achievements</h2>
          <button 
            className="text-gray-400 hover:text-white"
            onClick={() => setShowGoals(false)}
          >
            <X size={24} />
          </button>
        </div>

        {Object.entries(goalsByCategory).map(([category, goals]) => (
          <div key={category} className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-blue-300">{formatCategoryName(category)}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {goals.map(goal => {
                // Find the progress data for this goal
                const progressData = goalProgress.find(pg => pg.id === goal.id) || {
                  progress: 0,
                  percentage: 0,
                  isComplete: false,
                  isClaimed: false
                };
                
                const currentProgress = progressData.progress;
                const progressPercent = progressData.percentage;
                const isCompleted = progressData.isComplete;
                const isClaimed = progressData.isClaimed;

                return (
                  <div 
                    key={goal.id} 
                    className={`p-4 rounded-lg border ${isCompleted ? 'border-green-500 bg-gray-700' : 'border-gray-600 bg-gray-800'}`}
                  >
                    <div className="flex items-start">
                      <div className="text-2xl mr-3">{goal.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-bold">{goal.name}</h4>
                        <p className="text-sm text-gray-400 mb-2">{goal.description}</p>
                        
                        <div className="w-full bg-gray-700 rounded-full h-2.5 mb-2">
                          <div 
                            className={`h-2.5 rounded-full ${isCompleted ? 'bg-green-500' : 'bg-blue-600'}`} 
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                        
                        <div className="flex justify-between text-xs">
                          <span>{formatCurrency(currentProgress)} / {formatCurrency(goal.target)}</span>
                          <span>{Math.floor(progressPercent)}%</span>
                        </div>
                        
                        <div className="mt-3 flex justify-between items-center">
                          <div className="text-sm">
                            <span className="text-gray-400">Reward: </span>
                            <span className="text-green-400">{formatCurrency(goal.reward)}</span>
                          </div>
                          
                          {isCompleted && !isClaimed && (
                            <button
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm font-semibold"
                              onClick={() => claimGoal(goal.id)}
                            >
                              Claim +{formatCurrency(goal.reward)}
                            </button>
                          )}
                          {isCompleted && isClaimed && (
                            <span className="text-sm text-green-500 font-semibold">Claimed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GoalsModal;
